import { useEffect, useState } from 'react';
import { api } from '../../../shared/api.js';
import { Avatar } from '../../../layout/Avatar.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatDateTime } from '../../../shared/format.js';
import { usePosState } from '../hooks/usePosState.js';

export function ChildLinkPicker({ open, onClose }) {
  const setLink = usePosState((s) => s.setLink);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ('');
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/pos/active-children', { params: { q: term } });
        if (cancelled) return;
        setResults(data?.items || data?.data || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  if (!open) return null;

  function pick(s) {
    const child = s.childId || s.child || {};
    const card = s.cardId || s.card || {};
    const isSubscriber = s.linkKind === 'subscription';
    setLink({
      // المشترك خارج القاعة لا جلسة له — لا نختلق واحدة ولا نغيّر حالة وجوده.
      sessionId: isSubscriber ? null : (s._id || s.id),
      subscriptionOnly: isSubscriber,
      subscriptionPlan: isSubscriber && s.subscription ? s.subscription.planName : null,
      cardMissing: isSubscriber && !card.uid,
      cardUid: card.uid || s.cardUid || null,
      childId: child._id || child.id || null,
      childName: child.name || '—',
      photoUrl: child.photoUrl || null,
      cardBalance: typeof card.balance === 'number' ? card.balance : null,
      linkedBy: 'child_search'
    });
    onClose?.();
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <div
        className="card w-[520px] max-w-[92vw] p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold">بحث عن طفل</h3>
            <div className="text-xs text-ink-3 mt-0.5">
              بالاسم أو رقم ولي الأمر — يشمل الأطفال داخل القاعة والمشتركين شهرياً خارجها
            </div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-line">
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="اكتب الاسم..."
            autoFocus
          />
        </div>
        <div className="max-h-[360px] overflow-auto">
          {loading ? (
            <div className="text-center py-8 text-ink-3 text-[13px]">جاري البحث...</div>
          ) : results.length === 0 ? (
            <div className="text-center py-8 text-ink-3 text-[13px]">
              {q.trim() ? 'لا توجد نتائج' : 'اكتب اسم الطفل للبحث'}
            </div>
          ) : (
            results.map((s) => {
              const child = s.childId || s.child || {};
              const guardian = s.guardianId || s.guardian || {};
              const card = s.cardId || s.card || {};
              return (
                <button
                  key={s._id || s.id || `sub-${child._id}`}
                  type="button"
                  onClick={() => pick(s)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 border-b border-line hover:bg-paper-2 text-right"
                >
                  <Avatar size="md" name={child.name || '?'} id={child._id} src={child.photoUrl} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[13.5px] truncate">{child.name || '—'}</div>
                    <div className="text-[11px] text-ink-3 truncate">
                      <span dir="ltr">{guardian.phone || ''}</span>
                      {card.uid ? <span> • <span dir="ltr" style={{ fontFamily: 'monospace' }}>{card.uid}</span></span> : null}
                    </div>
                    {s.linkKind === 'subscription' ? (
                      <div className="text-[10.5px]" style={{ color: '#0E7490' }}>
                        اشتراك شهري{s.subscription && s.subscription.planName ? ` — ${s.subscription.planName}` : ''}
                        {' • '}
                        {card.uid
                          ? `رصيد الكارت ${Number(card.balance || 0).toLocaleString('en-US')} د.ع`
                          : (s.cardBlocked ? 'بطاقته غير صالحة للدفع' : 'لا توجد بطاقة مرتبطة')}
                        {' • خارج القاعة'}
                      </div>
                    ) : (
                      <div className="text-[10.5px] text-ink-3">منذ {formatDateTime(s.startTime)}</div>
                    )}
                  </div>
                  <Icon name="chevronL" size={14} />
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
